import React from 'react';
import { Heart, X, Clock, Flame, Calendar, ShoppingBasket, Check, ChefHat } from 'lucide-react';

// CSS-only iPhone-style mockup. Children renders the "app screen" content.
export const PhoneFrame = ({ children, className = '', tilt = 0, testId }) => (
  <div
    data-testid={testId}
    className={`relative ${className}`}
    style={{ transform: `rotate(${tilt}deg)` }}
  >
    <div className="relative w-[280px] sm:w-[300px] md:w-[320px] aspect-[9/19.5] rounded-[2.8rem] bg-stone-900 p-[10px] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.35)] ring-1 ring-stone-800">
      {/* dynamic island */}
      <div className="absolute left-1/2 top-3 -translate-x-1/2 z-20 h-[22px] w-[88px] rounded-full bg-stone-900" />
      <div className="relative h-full w-full overflow-hidden rounded-[2.2rem] bg-[#FFFDF8]">
        {children}
      </div>
    </div>
  </div>
);

// Screen 1 — Swipe / Match card (the hero phone)
export const ScreenMatch = () => (
  <div className="relative h-full w-full flex flex-col">
    {/* status bar */}
    <div className="flex items-center justify-between px-6 pt-3 pb-2 text-[10px] font-semibold text-stone-700">
      <span>9:41</span>
      <span className="flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-stone-700" />
        <span className="h-2 w-3 rounded-sm bg-stone-700" />
      </span>
    </div>
    {/* header */}
    <div className="flex items-center justify-between px-5 pb-3">
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-full bg-orange-500 flex items-center justify-center">
          <ChefHat className="h-4 w-4 text-white" strokeWidth={2.4} />
        </div>
        <span className="font-display font-bold text-stone-900 text-sm">MealMatcher</span>
      </div>
      <div className="h-7 w-7 rounded-full bg-stone-100 flex items-center justify-center">
        <span className="h-1 w-1 rounded-full bg-stone-500" />
        <span className="h-1 w-1 rounded-full bg-stone-500 mx-0.5" />
        <span className="h-1 w-1 rounded-full bg-stone-500" />
      </div>
    </div>

    {/* swipe card */}
    <div className="px-4 flex-1 flex flex-col">
      <div className="relative flex-1 rounded-[1.8rem] overflow-hidden shadow-[0_12px_32px_-12px_rgba(0,0,0,0.25)]">
        <img
          src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=600&q=80"
          alt="Recipe"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/85 via-stone-900/10 to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-bold text-orange-600">
          <Flame className="h-3 w-3" /> 96% match
        </div>
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <div className="flex items-center gap-2 text-[10px] font-semibold opacity-90 mb-1">
            <Clock className="h-3 w-3" /> 25 min
            <span className="opacity-50">·</span>
            <span>Middag</span>
          </div>
          <div className="font-display font-extrabold text-xl leading-tight">
            Sitruskylling med urtequinoa
          </div>
        </div>
      </div>

      {/* swipe actions */}
      <div className="flex items-center justify-center gap-5 py-5">
        <button className="h-12 w-12 rounded-full bg-white shadow-md border border-stone-200 flex items-center justify-center">
          <X className="h-5 w-5 text-stone-500" strokeWidth={2.6} />
        </button>
        <button className="h-14 w-14 rounded-full bg-orange-500 shadow-lg flex items-center justify-center ring-4 ring-orange-100">
          <Heart className="h-6 w-6 text-white fill-white" />
        </button>
        <button className="h-12 w-12 rounded-full bg-white shadow-md border border-stone-200 flex items-center justify-center">
          <Calendar className="h-5 w-5 text-stone-500" strokeWidth={2.4} />
        </button>
      </div>

      {/* bottom nav */}
      <div className="mx-auto mb-3 flex items-center gap-1.5 rounded-full bg-stone-900/95 px-3 py-1.5">
        {['•', '•', '•', '•'].map((d, i) => (
          <span
            key={i}
            className={`block h-1.5 rounded-full transition-all ${
              i === 0 ? 'w-6 bg-orange-400' : 'w-1.5 bg-stone-600'
            }`}
          />
        ))}
      </div>
    </div>
  </div>
);

// Screen 2 — Meal Planner
export const ScreenPlanner = () => {
  const days = ['M', 'T', 'O', 'T', 'F', 'L', 'S'];
  const meals = [
    { day: 'Man', name: 'Laks med dill', img: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&w=200&q=70', color: 'bg-rose-100' },
    { day: 'Tir', name: 'Pasta al limone', img: 'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?auto=format&fit=crop&w=200&q=70', color: 'bg-amber-100' },
    { day: 'Ons', name: 'Buddha bowl', img: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=200&q=70', color: 'bg-lime-100' },
  ];
  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex items-center justify-between px-5 pt-8 pb-3">
        <div>
          <div className="text-[10px] text-stone-500 font-medium">Mars 2025</div>
          <div className="font-display font-extrabold text-lg text-stone-900">Min uke</div>
        </div>
        <div className="h-8 w-8 rounded-full bg-orange-500 flex items-center justify-center">
          <span className="text-white text-lg font-bold leading-none">+</span>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 px-4 mb-3">
        {days.map((d, i) => (
          <div
            key={i}
            className={`flex flex-col items-center gap-1 py-2 rounded-xl ${
              i === 1 ? 'bg-orange-500 text-white' : 'text-stone-600'
            }`}
          >
            <span className="text-[9px] font-bold opacity-70">{d}</span>
            <span className="text-sm font-bold">{i + 17}</span>
          </div>
        ))}
      </div>
      <div className="px-4 space-y-2.5 overflow-hidden">
        {meals.map((m, i) => (
          <div key={i} className="flex items-center gap-3 rounded-2xl bg-white border border-stone-100 p-2.5 shadow-sm">
            <img src={m.img} alt="" className="h-12 w-12 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold text-orange-600 uppercase tracking-wider">
                {m.day}
              </div>
              <div className="font-semibold text-sm text-stone-900 truncate">{m.name}</div>
            </div>
            <Clock className="h-4 w-4 text-stone-400" />
          </div>
        ))}
      </div>
    </div>
  );
};

// Screen 3 — Shopping List
export const ScreenShopping = () => {
  const items = [
    { name: 'Kyllingfilet', qty: '500 g', done: true },
    { name: 'Sitron', qty: '2 stk', done: true },
    { name: 'Quinoa', qty: '200 g', done: false },
    { name: 'Fersk persille', qty: '1 bunt', done: false },
    { name: 'Hvitløk', qty: '3 fedd', done: false },
    { name: 'Olivenolje', qty: '4 ss', done: false },
  ];
  return (
    <div className="h-full w-full flex flex-col">
      <div className="px-5 pt-8 pb-3">
        <div className="text-[10px] text-stone-500 font-medium">14 ingredienser</div>
        <div className="font-display font-extrabold text-lg text-stone-900">Handleliste</div>
      </div>
      <div className="px-4 pb-2 flex items-center gap-2">
        <div className="flex-1 h-2 rounded-full bg-stone-100 overflow-hidden">
          <div className="h-full w-1/3 bg-orange-500 rounded-full" />
        </div>
        <span className="text-[10px] font-bold text-stone-700">2/6</span>
      </div>
      <div className="px-4 space-y-1.5">
        {items.map((it, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 rounded-xl p-2.5 ${
              it.done ? 'bg-stone-50' : 'bg-white border border-stone-100'
            }`}
          >
            <div
              className={`h-5 w-5 rounded-full flex items-center justify-center border-2 ${
                it.done ? 'bg-orange-500 border-orange-500' : 'border-stone-300'
              }`}
            >
              {it.done && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className={`text-sm font-semibold truncate ${
                  it.done ? 'text-stone-400 line-through' : 'text-stone-900'
                }`}
              >
                {it.name}
              </div>
            </div>
            <span className="text-[10px] font-bold text-stone-500">{it.qty}</span>
          </div>
        ))}
      </div>
      <div className="mt-auto px-4 pb-4">
        <button className="w-full rounded-full bg-stone-900 text-white text-xs font-bold py-3 flex items-center justify-center gap-2">
          <ShoppingBasket className="h-4 w-4" /> Eksporter liste
        </button>
      </div>
    </div>
  );
};

// Screen 4 — Discover/Filter
export const ScreenDiscover = () => {
  const cats = [
    { label: 'Fisk', emoji: '🐟', active: true },
    { label: 'Vegetar', emoji: '🥗', active: false },
    { label: 'Pasta', emoji: '🍝', active: false },
    { label: 'Kjøtt', emoji: '🥩', active: false },
  ];
  const recipes = [
    { img: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&w=300&q=70', time: '20 min', name: 'Ovnsbakt laks' },
    { img: 'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?auto=format&fit=crop&w=300&q=70', time: '25 min', name: 'Pasta limone' },
    { img: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=300&q=70', time: '15 min', name: 'Buddha bowl' },
    { img: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=300&q=70', time: '30 min', name: 'Pizza margherita' },
  ];
  return (
    <div className="h-full w-full flex flex-col">
      <div className="px-5 pt-8 pb-3">
        <div className="font-display font-extrabold text-lg text-stone-900">Oppdag</div>
        <div className="text-[10px] text-stone-500 font-medium">Filtrert for deg</div>
      </div>
      <div className="px-4 flex gap-2 overflow-hidden pb-3">
        {cats.map((c, i) => (
          <div
            key={i}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap ${
              c.active
                ? 'bg-orange-500 text-white shadow-sm'
                : 'bg-white border border-stone-200 text-stone-700'
            }`}
          >
            <span>{c.emoji}</span>
            <span>{c.label}</span>
          </div>
        ))}
      </div>
      <div className="px-4 grid grid-cols-2 gap-2.5">
        {recipes.map((r, i) => (
          <div key={i} className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-sm">
            <img src={r.img} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-transparent" />
            <div className="absolute top-2 right-2 h-6 w-6 rounded-full bg-white/90 backdrop-blur flex items-center justify-center">
              <Heart className="h-3 w-3 text-orange-500" />
            </div>
            <div className="absolute bottom-2 left-2 right-2 text-white">
              <div className="text-[8px] font-bold opacity-80 flex items-center gap-1">
                <Clock className="h-2.5 w-2.5" /> {r.time}
              </div>
              <div className="text-[11px] font-display font-extrabold leading-tight truncate">
                {r.name}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Screen 5 — Recipe Detail
export const ScreenRecipe = () => (
  <div className="h-full w-full flex flex-col">
    <div className="relative h-44 w-full">
      <img
        src="https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&w=600&q=80"
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 to-transparent" />
      <div className="absolute top-8 left-4 h-7 w-7 rounded-full bg-white/90 flex items-center justify-center">
        <span className="text-stone-700 text-lg leading-none">‹</span>
      </div>
      <div className="absolute bottom-3 left-4 right-4 text-white">
        <div className="text-[10px] font-bold opacity-80 flex items-center gap-1.5">
          <Clock className="h-3 w-3" /> 25 min · Middag
        </div>
        <div className="font-display font-extrabold text-lg leading-tight">
          Ovnsbakt laks med dill
        </div>
      </div>
    </div>
    <div className="px-4 py-3 grid grid-cols-4 gap-2 border-b border-stone-100">
      {[
        { label: 'Kcal', v: '420' },
        { label: 'Protein', v: '38g' },
        { label: 'Fett', v: '22g' },
        { label: 'Karbo', v: '14g' },
      ].map((n, i) => (
        <div key={i} className="text-center">
          <div className="text-[9px] font-bold text-stone-400 uppercase">{n.label}</div>
          <div className="text-xs font-extrabold text-stone-900">{n.v}</div>
        </div>
      ))}
    </div>
    <div className="px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-extrabold text-sm text-stone-900">Ingredienser</div>
        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-[10px] font-bold text-orange-600">
          2 porsjoner
        </div>
      </div>
      <div className="space-y-1.5">
        {[
          ['Laksefilet', '300 g'],
          ['Dill', '1 bunt'],
          ['Sitron', '1 stk'],
          ['Olivenolje', '2 ss'],
        ].map(([n, q], i) => (
          <div key={i} className="flex items-center justify-between text-[11px]">
            <span className="text-stone-700">{n}</span>
            <span className="font-bold text-stone-900">{q}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="mt-auto px-4 pb-3">
      <button className="w-full rounded-full bg-orange-500 text-white text-xs font-bold py-3">
        Legg til i planlegger
      </button>
    </div>
  </div>
);
